import { formatDisplayDateTime } from "../../utils/dateUtils";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { ActivityNotAllowedICDAxiosInstance } from "../../Network/activityNotAllowedICDAxiosInstance";
import {
  activityNotAllowedICD,
  Datum,
} from "../../interface/ActivityNotAllowedICD";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustomIcd from "../Shared/ListofcustemIcd";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityNotAllowedICD/ActivityNotAllowedICD";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialtyName from "../Shared/Specialty";
import useQaAutomationIds from "../../utils/useQaAutomationIds";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

interface CodeWithType {
  code: string | number;
  type: string;
}

type ActivitiesByType = { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] };
const EMPTY_BY_TYPE: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const TYPE_MAP: Record<string, string> = {
  cpt: "CPT", CPT: "CPT",
  hcpcs: "HCPCS", HCPCS: "HCPCS",
  dental: "Dental", Dental: "Dental",
  drugs: "Drugs", Drugs: "Drugs",
};

const getActivityTypeColor = (type: string) => {
  const tw: Record<string, { bg: string; border: string; text: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900"   },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900"  },
    Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
  };
  return tw[type] || { bg: "bg-gray-100", border: "border-gray-500", text: "text-gray-900" };
};

const flattenByType = (byType: ActivitiesByType): CodeWithType[] => {
  const result: CodeWithType[] = [];
  Object.entries(byType).forEach(([type, options]) => {
    options.forEach((o) => result.push({ code: o.value as string, type }));
  });
  return result;
};

const groupByType = (items: CodeWithType[]): ActivitiesByType => {
  const grouped: ActivitiesByType = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
  items.forEach(({ code, type }) => {
    const key = TYPE_MAP[type] as keyof ActivitiesByType;
    if (key) grouped[key].push({ value: code, label: code.toString() });
  });
  return grouped;
};


const modeFromString = (val: string | boolean | undefined | null): "EXCLUSION" | "INCLUSION" | null => {
  if (val === "EXCLUDED" || val === true)  return "EXCLUSION";
  if (val === "INCLUDED")                  return "INCLUSION";
  return null;
};


const getSqlId = (value: any): number | null => {
  if (value === null || value === undefined) return null;

  if (typeof value === "object") {
    const candidate =
      value.dbId ??
      value.db_id ??
      value.id ??
      value.value?.dbId ??
      value.value?.db_id ??
      value.value?.id ??
      value.value;

    if (
      candidate !== null &&
      candidate !== undefined &&
      typeof candidate !== "object"
    ) {
      const id = Number(candidate);
      return Number.isInteger(id) && id > 0 ? id : null;
    }

    return null;
  }

  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const toNumericSqlIds = (values: any[] = []): number[] =>
  values
    .map((value) => getSqlId(value))
    .filter((id): id is number => id !== null);

const toStringSqlIds = (values: any[] = []): string[] =>
  toNumericSqlIds(values).map(String);

const getPolicySqlIds = (values: any[] = []): number[] =>
  values
    .map((policy: any) =>
      getSqlId(
        policy?.id ??
          policy?.dbId ??
          policy?.db_id ??
          policy?.value?.id ??
          (typeof policy?.value !== "object" ? policy?.value : null)
      )
    )
    .filter((id): id is number => id !== null);

const firstText = (...values: any[]): string => {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      typeof value !== "object"
    ) {
      const text = String(value).trim();
      if (text) return text;
    }
  }
  return "";
};

const formatSqlEntityLabel = (item: any, ...nameCandidates: any[]): string => {
  if (item === null || item === undefined) return "";
  if (typeof item !== "object") return String(item);

  const id = getSqlId(item);
  const name = firstText(...nameCandidates);

  if (id !== null) return name ? `(${id}) ${name}` : `(${id})`;
  return name;
};

// Retain entity activity metadata from GET-by-id when mapping Edit defaults.
const entityActivity = (entry: any) => {
  if (!entry || typeof entry !== "object") return {};
  const nested = entry.value && typeof entry.value === "object" ? entry.value : {};
  const raw = entry.is_active ?? entry.isActive ?? entry.active ?? nested.is_active ??
    nested.isActive ?? nested.active;
  const state = entry.status ?? entry.Status ?? nested.status ?? nested.Status;
  const deleted = entry.is_deleted ?? entry.isDeleted ?? nested.is_deleted ?? nested.isDeleted;
  const isFalse = (v: any) => v === false || v === 0 || v === "0" ||
    (typeof v === "string" && ["false", "inactive", "disabled"].includes(v.trim().toLowerCase()));
  const isTrue = (v: any) => v === true || v === 1 || v === "1" ||
    (typeof v === "string" && ["true", "active", "enabled"].includes(v.trim().toLowerCase()));
  const inactive = isTrue(deleted) || isFalse(raw) || isFalse(state);
  return {
    ...(raw !== undefined ? { is_active: raw } : {}),
    ...(state !== undefined ? { status: state } : {}),
    ...(deleted !== undefined ? { is_deleted: deleted } : {}),
    ...(inactive ? { __inactive: true } : {}),
  };
};

const linkedDisplay = (label: string, entity: any): string =>
  `${label}${entityActivity(entity).__inactive ? " (Inactive)" : ""}`;

const formatPatientLabel = (patient: any): string => {
  if (patient === null || patient === undefined) return "";
  if (typeof patient !== "object") return String(patient);

  const name = firstText(
    patient.full_name,
    patient.Full_name,
    patient.name,
    patient.Patient_name,
    patient.patient_name,
    [patient.First_name ?? patient.first_name, patient.Last_name ?? patient.last_name]
      .filter(Boolean)
      .join(" ")
  );

  const nationalId = firstText(patient.National_id, patient.national_id);
  const memberId = firstText(patient.Member_id, patient.member_id);
  const passport = firstText(
    patient.Patient_passport,
    patient.patient_passport,
    patient.passport
  );

  const identifiers = [
    nationalId ? `National ID: ${nationalId}` : "",
    memberId ? `Member ID: ${memberId}` : "",
    passport ? `Passport: ${passport}` : "",
  ].filter(Boolean);

  return identifiers.length > 0
    ? `${name || "Patient"} (${identifiers.join(" | ")})`
    : name || "Patient";
};

const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
};

// ─── EntityCard ───────────────────────────────────────────────────────────────
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200"    },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200"    },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200"  },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200"   },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200"    },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200"  },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200"  },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200"   },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200"    },
};

interface EntityCardProps {
  label: string; icon: string; color: string;
  isActive: boolean; mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void; onInclusion: () => void; onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({ label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children }) => {
  const c = entityColors[color] ?? entityColors.slate;
  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => { if (scrollable) scrollable.scrollTop = scrollTop; });
  };
  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button type="button" onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10" title="Clear">
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button type="button" onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Exclude
          </button>
          <button type="button" onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}>
            Include
          </button>
        </div>
      </div>
      {isActive && <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">{children}</div>}
    </div>
  );
};

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── Generic wide segmented-control picker (3 tabs + content) ─────────────────
const SourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  options: { key: string; label: string; icon: string; count: number }[];
  children: React.ReactNode;
}> = ({ title, selected, onSelect, options, children }) => (
  <div>
    <Label className="mb-2 block">{title}</Label>
    <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
      {options.map((opt) => {
        const isSelected = selected === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(isSelected ? null : opt.key)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <i className={`fa-solid ${opt.icon} text-xs`}></i>
            <span>{opt.label}</span>
            {opt.count > 0 && (
              <span className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
    {selected && (
      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {children}
      </div>
    )}
  </div>
);
// ─── Activity Badge Cell ──────────────────────────────────────────────────────
const ActivityCell: React.FC<{
  items: CodeWithType[];
  medicalGroups: string[];
  customGroups: string[];
  searchTerm?: string;
  onShowAll: (items: CodeWithType[], medical: string[], custom: string[]) => void;
}> = ({ items, medicalGroups, customGroups, searchTerm = "", onShowAll }) => {
  const totalCount = items.length + medicalGroups.length + customGroups.length;

  if (totalCount === 0) {
    return <div className="w-full text-center text-gray-400">—</div>;
  }

  const allDisplay = [
    ...items.map((item) => ({
      label: item.code.toString(),
      type: item.type,
      isGroup: false,
      originalItem: item,
    })),
    ...medicalGroups.map((group) => ({
      label: group,
      type: "Medical",
      isGroup: true,
      originalItem: null,
    })),
    ...customGroups.map((group) => ({
      label: group,
      type: "Custom",
      isGroup: true,
      originalItem: null,
    })),
  ];

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const hasSearch = normalizedSearch.length >= 2;

  const matchingDisplay = hasSearch
    ? allDisplay.filter((entry) =>
        entry.label.toLowerCase().includes(normalizedSearch),
      )
    : [];

  const hasMatches = hasSearch && matchingDisplay.length > 0;
  const displaySource = hasMatches ? matchingDisplay : allDisplay;
  const displayedValues = displaySource.slice(0, 3);

  const remainingCount = Math.max(
    0,
    displaySource.length - displayedValues.length,
  );

  const dialogItems = hasMatches
    ? matchingDisplay
        .filter((entry) => !entry.isGroup && entry.originalItem)
        .map((entry) => entry.originalItem as CodeWithType)
    : items;

  const dialogMedical = hasMatches
    ? matchingDisplay
        .filter((entry) => entry.isGroup && entry.type === "Medical")
        .map((entry) => entry.label)
    : medicalGroups;

  const dialogCustom = hasMatches
    ? matchingDisplay
        .filter((entry) => entry.isGroup && entry.type === "Custom")
        .map((entry) => entry.label)
    : customGroups;

  return (
    <div className="w-full px-2 py-1.5">
      <div className="flex flex-wrap justify-center gap-1.5 mb-1.5">
        {displayedValues.map((entry, index) => {
          const isMatching =
            hasSearch &&
            entry.label.toLowerCase().includes(normalizedSearch);

          const defaultColor = entry.isGroup
            ? entry.type === "Medical"
              ? {
                  bg: "bg-slate-50",
                  border: "border-slate-400",
                  text: "text-slate-700",
                }
              : {
                  bg: "bg-blue-50",
                  border: "border-blue-300",
                  text: "text-blue-800",
                }
            : getActivityTypeColor(entry.type);

          const color = isMatching
            ? {
                bg: "bg-yellow-200",
                border: "border-yellow-400",
                text: "text-yellow-900",
              }
            : defaultColor;

          return (
            <div
              key={`${entry.type}-${entry.label}-${index}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${color.bg} ${color.border}`}
            >
              <span className={`font-semibold text-xs ${color.text}`}>
                {entry.label}
              </span>
              <span
                className={`text-[9px] font-bold uppercase ${color.text} opacity-75`}
              >
                {entry.type}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 pt-1">
        <span className="text-xs font-bold text-gray-500">
          {hasMatches
            ? `${matchingDisplay.length}/${allDisplay.length}`
            : totalCount}
        </span>

        {remainingCount > 0 && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              onShowAll(dialogItems, dialogMedical, dialogCustom);
            }}
            className={`text-xs font-semibold ${
              hasMatches
                ? "text-yellow-600 hover:text-yellow-800"
                : "text-blue-600 hover:text-blue-800"
            }`}
          >
            +{remainingCount} more →
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ActivityNotAllowedICD: React.FC = () => {
  useQaAutomationIds("activity-not-allowed-icd");
  const [items, setItems] = useState<activityNotAllowedICD>({ data: [] });

  // ─── Role ─────────────────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ─── Force Dialog ──────────────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceAction, setForceAction] = useState<"hardDelete" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  // ─── Form states ───────────────────────────────────────────────────────────
  const [relationName, setRelationName] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [selectedMainOption, setSelectedMainOption] = useState<string | null>(null);
  const [activitiesByType, setActivitiesByType] = useState<ActivitiesByType>({ ...EMPTY_BY_TYPE });
  const [ActivityMedicalGroups, setActivityMedicalGroups] = useState<Option[]>([]);
  const [ActivityCustomGroups, setActivityCustomGroups] = useState<Option[]>([]);

  const [selectedAllowedOption, setSelectedAllowedOption] = useState<string | null>(null);
  const [icd, setIcd] = useState<Option[]>([]);
  const [icdMedicalGroups, setIcdMedicalGroups] = useState<Option[]>([]);
  const [icdCustomGroups, setIcdCustomGroups] = useState<Option[]>([]);
  const [IcdDescription] = useState<string>("");

  const [showEntities, setShowEntities] = useState(false);

  const [provider, setProvider] = useState<Option[]>([]);
  const [providerMode, setProviderMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive, setIsProviderActive] = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);
  const [clearProvider, setClearProvider] = useState(false);

  const [doctor, setDoctor] = useState<Option[]>([]);
  const [doctorMode, setDoctorMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive, setIsDoctorActive] = useState(false);
  const [selectAllDoctor, setSelectAllDoctor] = useState(false);
  const [clearDoctor, setClearDoctor] = useState(false);

  const [networks, setNetworks] = useState<Option[]>([]);
  const [networkMode, setNetworkMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive, setIsNetworkActive] = useState(false);
  const [selectAllNetworks, setSelectAllNetworks] = useState(false);
  const [clearNetworks, setClearNetworks] = useState(false);

  const [insuranceplan, setInsuranceplan] = useState<Option[]>([]);
  const [insuranceMode, setInsuranceMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive, setIsInsuranceActive] = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);

  const [Payer, setPayer] = useState<string[]>([]);
  const [payerMode, setPayerMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive, setIsPayerActive] = useState(false);
  const [selectAllPayer, setSelectAllPayer] = useState(false);

  const [policy, setPolicy] = useState<PolicyItem[]>([]);
  const [policyMode, setPolicyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive, setIsPolicyActive] = useState(false);
  const [selectAllPolicy, setSelectAllPolicy] = useState(false);
  const [clearPolicy, setClearPolicy] = useState(false);

  const [patients, setPatients] = useState<Option[]>([]);
  const [patientMode, setPatientMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive, setIsPatientActive] = useState(false);
  const [selectAllPatients, setSelectAllPatients] = useState(false);
  const [clearPatient, setClearPatient] = useState(false);

  const [vip, setVip] = useState<Option[]>([]);
  const [vipMode, setVipMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive, setIsVipActive] = useState(false);
  const [selectAllVip, setSelectAllVip] = useState(false);
  const [clearVip, setClearVip] = useState(false);

  const [userLevel, setUserLevel] = useState<string[]>([]);
  const [userMode, setUserMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive, setIsUserActive] = useState(false);
  const [selectAllUser, setSelectAllUser] = useState(false);

  const [specialty, setSpecialty] = useState<Option[]>([]);
  const [specialtyMode, setSpecialtyMode] = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive, setIsSpecialtyActive] = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);
  const [clearSpecialty, setClearSpecialty] = useState(false);

  const [clearIcd, setClearIcd] = useState(false);
  const [clearIcdMedicalGroups, setClearIcdMedicalGroups] = useState(false);
  const [clearIcdCustomGroups, setClearIcdCustomGroups] = useState(false);
  const [clearActivityMedicalGroups, setClearActivityMedicalGroups] = useState(false);
  const [clearActivityCustomGroups, setClearActivityCustomGroups] = useState(false);

  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [icdCodesDialogOpen, setIcdCodesDialogOpen] = useState(false);
  const [icdCodesDialogList, setIcdCodesDialogList] = useState<Array<{ label: string; kind: "icd" | "medical" | "custom" }>>([]);
  const [icdCodesDialogTitle, setIcdCodesDialogTitle] = useState("");

  const [filterStatus, setFilterStatus] = useState<string>("");

  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");

  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [allCodesOpen, setAllCodesOpen] = useState(false);
  const [allCodesItems, setAllCodesItems] = useState<CodeWithType[]>([]);
  const [allCodesTitle, setAllCodesTitle] = useState("");
  const [allCodesMedical, setAllCodesMedical] = useState<string[]>([]);
  const [allCodesCustom, setAllCodesCustom] = useState<string[]>([]);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  const cardModeToString = (
    isActive: boolean, mode: "EXCLUSION" | "INCLUSION" | null, selectAll: boolean
  ): "INCLUDED" | "EXCLUDED" | "DISABLED" => {
    if (!isActive && !selectAll) return "DISABLED";
    if (mode === "EXCLUSION") return "EXCLUDED";
    if (mode === "INCLUSION") return "INCLUDED";
    return "INCLUDED";
  };

  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();

  const normalizeItem = (item: any): Datum & { activity_flat: CodeWithType[] } => {
    // The current backend returns the dedicated Activity/ICD handlers using
    // names such as activity_code / icd_codes / activity_medical_groups.
    // Older frontend code expects activity / icd / activity_medicalgroups.
    // Normalize both contracts here once so Table, View and Edit all use the
    // same canonical frontend fields.

    const firstArray = (...values: any[]): any[] => {
      // Prefer an actual non-empty backend value. This is important when a
      // fallback table row contains activity: [] but GET-by-id returns
      // activity_code: ["0236T"].
      for (const value of values) {
        if (Array.isArray(value) && value.length > 0) return value;
      }
      for (const value of values) {
        if (Array.isArray(value)) return value;
      }
      for (const value of values) {
        if (value !== undefined && value !== null && value !== "") return [value];
      }
      return [];
    };

    const actCodes = firstArray(
      item.activity_code,
      item.activity_codes,
      item.activity,
    ).map((value: any) =>
      typeof value === "object" && value !== null
        ? String(value.code ?? value.activity_code ?? value.activity ?? value.value ?? "")
        : String(value),
    ).filter(Boolean);

    const rawActivityTypes = firstArray(
      item.activity_type,
      item.activity_types,
    );

    const actTypes: string[] =
      rawActivityTypes.length > 0
        ? actCodes.map((_, index) =>
            String(rawActivityTypes[index] ?? rawActivityTypes[0] ?? "cpt"),
          )
        : actCodes.map(() => "cpt");

    const activityMedicalGroups = firstArray(
      item.activity_medical_groups,
      item.activity_medicalgroups,
    );

    const activityCustomGroups = firstArray(
      item.activity_custom_groups,
      item.activity_customgroups,
    );

    const icdCodes = firstArray(
      item.icd_codes,
      item.icd_code,
      item.icd,
    ).map((value: any) =>
      typeof value === "object" && value !== null
        ? String(value.ICD ?? value.icd ?? value.code ?? value.value ?? "")
        : String(value),
    ).filter(Boolean);

    const icdMedicalGroups = firstArray(
      item.icd_medical_groups,
      item.icd_medicalgroups,
    );

    const icdCustomGroups = firstArray(
      item.icd_custom_groups,
      item.icd_customgroups,
    );

    const activity_flat: CodeWithType[] = actCodes.map((code, idx) => ({
      code,
      type: TYPE_MAP[(actTypes[idx] || "cpt").toLowerCase()] || "CPT",
    }));

    const modeStr = (val: any): boolean => val === "EXCLUDED";

    return {
      ...item,

      // Canonical aliases consumed by the existing Table / View / Edit code.
      activity: actCodes,
      activity_type: actTypes,
      activity_medicalgroups: activityMedicalGroups,
      activity_customgroups: activityCustomGroups,
      icd: icdCodes,
      icd_medicalgroups: icdMedicalGroups,
      icd_customgroups: icdCustomGroups,
      activity_flat,

      networks:      item.networks      ?? item.network   ?? [],
      patients:      item.patients      ?? item.patient   ?? [],
      insuranceplan: item.insuranceplan ?? item.insurance ?? [],
      apply_to_other_providers:     modeStr(item.provider_mode),
      apply_to_other_doctor:        modeStr(item.doctor_mode),
      apply_to_other_networks:      modeStr(item.network_mode),
      apply_to_other_insuranceplan: modeStr(item.insuranceplan_mode),
      apply_to_other_payer:         modeStr(item.payer_mode),
      apply_to_other_patients:      modeStr(item.patient_mode),
      apply_to_other_policy:        modeStr(item.policy_mode),
      apply_to_other_user:          modeStr(item.user_mode),
      apply_to_vip:                 modeStr(item.vip_mode),
      apply_to_other_specialty:     modeStr(item.specialty_mode),
      select_all_providers:     item.provider_all      ?? false,
      select_all_doctor:        item.doctor_all        ?? false,
      select_all_networks:      item.network_all       ?? false,
      select_all_insuranceplan: item.insuranceplan_all ?? false,
      select_all_payer:         item.payer_all         ?? false,
      select_all_patients:      item.patient_all       ?? false,
      select_all_policy:        item.policy_all        ?? false,
      select_all_user:          item.user_all          ?? false,
      select_all_vip:           item.vip_all           ?? false,
      select_all_specialty:     item.specialty_all     ?? false,
      specialty: item.specialty ?? [],
    };
  };


  useEffect(() => { fetchUsers(); }, []);

  const resetAllEntityStates = () => {
    setProvider([]);    setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setDoctor([]);      setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setNetworks([]);    setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);       setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPolicy([]);      setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setPatients([]);    setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setVip([]);         setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setUserLevel([]);   setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setSpecialty([]);   setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  const validateEntities = (): string | null => {
    if (!showEntities) return null;
    const checks = [
      { isActive: isProviderActive,   hasData: selectAllProviders  || provider.length > 0,     label: "Providers"      },
      { isActive: isDoctorActive,     hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor"         },
      { isActive: isNetworkActive,    hasData: selectAllNetworks   || networks.length > 0,      label: "Networks"       },
      { isActive: isInsuranceActive,  hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,     hasData: selectAllPolicy     || policy.length > 0,        label: "Policy"         },
      { isActive: isPatientActive,    hasData: selectAllPatients   || patients.length > 0,      label: "Patients"       },
      { isActive: isVipActive,        hasData: selectAllVip        || vip.length > 0,           label: "VIP"            },
      { isActive: isPayerActive,      hasData: selectAllPayer      || Payer.length > 0,         label: "Payer"          },
      { isActive: isUserActive,       hasData: selectAllUser       || userLevel.length > 0,     label: "User Level"     },
      { isActive: isSpecialtyActive,  hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty"      },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length > 0) return `The following cards are open but have no data: ${empty.join(", ")}. Please select data or close them.`;
    const anyActive = checks.some(c => c.isActive);
    if (!anyActive) return "Entities Configuration is open but no entity card is selected. Please activate at least one entity or turn off the toggle.";
    return null;
  };

  const clear = () => {
    setRelationName("");
    setActivitiesByType({ ...EMPTY_BY_TYPE });
    setActivityMedicalGroups([]); setActivityCustomGroups([]);
    setSelectedMainOption(null);
    setIcd([]); setIcdMedicalGroups([]); setIcdCustomGroups([]);
    setSelectedAllowedOption(null);
    setIsActive(true); setShowEntities(false);
    resetAllEntityStates();
    setClearIcd(true); setClearIcdMedicalGroups(true); setClearIcdCustomGroups(true);
    setClearActivityMedicalGroups(true); setClearActivityCustomGroups(true);
    setClearProvider(true); setClearDoctor(true); setClearNetworks(true);
    setClearInsuranceplan(true); setClearPatient(true); setClearPolicy(true);
    setClearVip(true); setClearSpecialty(true);
  };

  const buildPayload = () => {
    const flat = flattenByType(activitiesByType);
    return {
      relation_name:          relationName,
      activity:               flat.map((c) => c.code),
      activity_type:          flat.map((c) => c.type.toLowerCase()),
      types:                  Array.from(new Set(flat.map((c) => c.type.toLowerCase()))),
      activity_medicalgroups: ActivityMedicalGroups.map((o) => o.value),
      activity_customgroups:  ActivityCustomGroups.map((o) => o.value),
      icd:                    icd.map((o) => o.value),
      icd_medicalgroups:      icdMedicalGroups.map((o) => o.value),
      icd_customgroups:       icdCustomGroups.map((o) => o.value),
      provider:      toStringSqlIds(provider),
      provider_mode: cardModeToString(isProviderActive, providerMode, selectAllProviders),
      provider_all:  selectAllProviders,
      doctor:        toStringSqlIds(doctor),
      doctor_mode:   cardModeToString(isDoctorActive, doctorMode, selectAllDoctor),
      doctor_all:    selectAllDoctor,
      networks:      toNumericSqlIds(networks),
      network_mode:  cardModeToString(isNetworkActive, networkMode, selectAllNetworks),
      network_all:   selectAllNetworks,
      insuranceplan:      toNumericSqlIds(insuranceplan),
      insuranceplan_mode: cardModeToString(isInsuranceActive, insuranceMode, selectAllInsurance),
      insuranceplan_all:  selectAllInsurance,
      payer:      Payer,
      payer_mode: cardModeToString(isPayerActive, payerMode, selectAllPayer),
      payer_all:  selectAllPayer,
      patients:      toNumericSqlIds(patients),
      patient_mode:  cardModeToString(isPatientActive, patientMode, selectAllPatients),
      patient_all:   selectAllPatients,
      policy:      getPolicySqlIds(policy),
      policy_mode: cardModeToString(isPolicyActive, policyMode, selectAllPolicy),
      policy_all:  selectAllPolicy,
      user_level:  userLevel,
      user_mode:   cardModeToString(isUserActive, userMode, selectAllUser),
      user_all:    selectAllUser,
      vip:      toNumericSqlIds(vip),
      vip_mode: cardModeToString(isVipActive, vipMode, selectAllVip),
      vip_all:  selectAllVip,
      specialty:      toNumericSqlIds(specialty),
      specialty_mode: cardModeToString(isSpecialtyActive, specialtyMode, selectAllSpecialty),
      specialty_all:  selectAllSpecialty,
      is_active: isActive,
    };
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityNotAllowedICDAxiosInstance.get(
        "getActivityNotAllowedWithIcd",
        {
          params: {
            page,
            limit,
            name: filters.search.trim() || undefined,
            activity: filters.search.trim() || undefined,
            disease: filters.search.trim() || undefined,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ data: rows }),
    normalizeRow: normalizeItem,
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(a.id) - Number(b.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    onError: (error) => {
      console.error("Failed to fetch Activity Not Allowed ICD", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activity Not Allowed ICD",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => { if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id); });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const extractActivityNotAllowedDetail = (payload: any): any | null => {
    const candidates = [
      payload?.result,
      payload?.getActivityNotAllowedIcds,
      payload?.getActivityNotAllowedWithIcd,
      payload?.data,
      payload,
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      if (Array.isArray(candidate)) {
        const row = candidate.find(
          (entry: any) =>
            entry &&
            typeof entry === "object" &&
            (entry.id != null ||
              entry.relation_name != null ||
              Array.isArray(entry.activity) ||
              Array.isArray(entry.activity_code) ||
              Array.isArray(entry.icd) ||
              Array.isArray(entry.icd_codes)),
        );
        if (row) return row;
        continue;
      }

      if (typeof candidate === "object") {
        if (
          candidate.id != null ||
          candidate.relation_name != null ||
          Array.isArray(candidate.activity) ||
          Array.isArray(candidate.activity_code) ||
          Array.isArray(candidate.icd) ||
          Array.isArray(candidate.icd_codes)
        ) {
          return candidate;
        }

        for (const key of [
          "result",
          "data",
          "getActivityNotAllowedIcds",
          "getActivityNotAllowedWithIcd",
        ]) {
          const nested = candidate[key];
          if (Array.isArray(nested) && nested.length > 0) return nested[0];
          if (nested && typeof nested === "object") return nested;
        }
      }
    }

    return null;
  };

  const fetchActivityNotAllowedDetails = async (
    id: string | number,
    fallbackItem?: Datum,
  ): Promise<Datum> => {
    const response = await ActivityNotAllowedICDAxiosInstance.get(
      `getActivityNotAllowedWithIcd/${id}`,
    );

    const detail = extractActivityNotAllowedDetail(response.data);
    const merged = detail
      ? {
          ...(fallbackItem ? normalizeItem(fallbackItem) : {}),
          ...detail,
        }
      : fallbackItem;

    if (!merged) {
      throw new Error("Activity Not Allowed ICD details were not returned by the backend.");
    }

    return normalizeItem(merged);
  };

  const handleOpenDetails = async (item: Datum) => {
    try {
      const detail = await fetchActivityNotAllowedDetails(item.id, item);
      setSelectedItem(detail);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to load Activity Not Allowed ICD details.",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = async (item: Datum) => {
    try {
      const detail = await fetchActivityNotAllowedDetails(item.id, item);
      handleEdit(detail);
      setIsDialogOpen(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "Failed to load Activity Not Allowed ICD for editing.",
        variant: "destructive",
      });
    }
  };

  // ─── Soft Delete ──────────────────────────────────────────────────────────
  // Swagger:
  // DELETE /ActivityNotAllowedWithIcd/softDeleteActivityNotAllowedWithIcd/{id}
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedICDAxiosInstance.delete(
        `softDeleteActivityNotAllowedWithIcd/${id}`,
        { data: { token } },
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: "Activity Not Allowed ICD Deleted Successfully.",
        });
        return;
      }

      toast({
        title: "Error",
        description:
          response.data?.error ||
          response.data?.message ||
          "Failed to soft delete Activity Not Allowed ICD.",
        variant: "destructive",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to soft delete Activity Not Allowed ICD.",
        variant: "destructive",
      });
    }
  };

  // ─── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedICDAxiosInstance.patch(
        `restoreActivityNotAllowedWithIcd/${id}`, { token }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Not Allowed ICD restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─── Hard Delete ──────────────────────────────────────────────────────────
  // Swagger:
  // DELETE /ActivityNotAllowedWithIcd/deleteActivityNotAllowedWithIcd/{id}
  const permanentDeleteItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedICDAxiosInstance.delete(
        `deleteActivityNotAllowedWithIcd/${id}`,
        { data: { token } },
      );

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);
        toast({
          title: "Success",
          description: "Activity Not Allowed ICD Permanently Deleted.",
        });
        return;
      }

      setForceError(
        response.data?.error ||
          response.data?.message ||
          "Failed to permanently delete Activity Not Allowed ICD.",
      );
    } catch (error: any) {
      setForceError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to permanently delete Activity Not Allowed ICD.",
      );
    }
  };

  // ─── Toggle Status ────────────────────────────────────────────────────────
  const toggleActiveStatus = async (item: Datum) => {
    try {
      // Always load the complete record first. The paginated list row may not
      // contain all relation/entity fields, and sending it directly to UPDATE
      // can unintentionally clear backend data.
      const fresh = await fetchActivityNotAllowedDetails(item.id, item);
      const flat: CodeWithType[] = (fresh as any).activity_flat || [];

      const payload = {
        data: {
          id: fresh.id,
          relation_name: fresh.relation_name,
          activity:
            Array.isArray((fresh as any).activity) &&
            (fresh as any).activity.length > 0
              ? (fresh as any).activity
              : flat.map((c) => c.code),
          activity_type:
            Array.isArray((fresh as any).activity_type) &&
            (fresh as any).activity_type.length > 0
              ? (fresh as any).activity_type
              : flat.map((c) => c.type.toLowerCase()),
          types:
            Array.isArray((fresh as any).types) &&
            (fresh as any).types.length > 0
              ? (fresh as any).types
              : Array.from(new Set(flat.map((c) => c.type.toLowerCase()))),
          activity_medicalgroups: toNumericSqlIds(fresh.activity_medicalgroups || []),
          activity_customgroups: toNumericSqlIds(fresh.activity_customgroups || []),
          icd: fresh.icd || [],
          icd_medicalgroups: toNumericSqlIds(fresh.icd_medicalgroups || []),
          icd_customgroups: toNumericSqlIds(fresh.icd_customgroups || []),

          provider: toStringSqlIds(fresh.provider || []),
          provider_mode: (fresh as any).provider_mode ?? "DISABLED",
          provider_all: (fresh as any).provider_all ?? false,

          doctor: toStringSqlIds(fresh.doctor || []),
          doctor_mode: (fresh as any).doctor_mode ?? "DISABLED",
          doctor_all: (fresh as any).doctor_all ?? false,

          specialty: toNumericSqlIds(fresh.specialty || []),
          specialty_mode: (fresh as any).specialty_mode ?? "DISABLED",
          specialty_all: (fresh as any).specialty_all ?? false,

          networks: toNumericSqlIds((fresh as any).networks ?? (fresh as any).network ?? []),
          network_mode: (fresh as any).network_mode ?? "DISABLED",
          network_all: (fresh as any).network_all ?? false,

          insuranceplan: toNumericSqlIds(fresh.insuranceplan || []),
          insuranceplan_mode:
            (fresh as any).insuranceplan_mode ?? "DISABLED",
          insuranceplan_all:
            (fresh as any).insuranceplan_all ?? false,

          payer: fresh.payer || [],
          payer_mode: (fresh as any).payer_mode ?? "DISABLED",
          payer_all: (fresh as any).payer_all ?? false,

          patients: toNumericSqlIds((fresh as any).patients ?? (fresh as any).patient ?? []),
          patient_mode: (fresh as any).patient_mode ?? "DISABLED",
          patient_all: (fresh as any).patient_all ?? false,

          policy: getPolicySqlIds(fresh.policy || []),
          policy_mode: (fresh as any).policy_mode ?? "DISABLED",
          policy_all: (fresh as any).policy_all ?? false,

          user_level: fresh.user_level || [],
          user_mode: (fresh as any).user_mode ?? "DISABLED",
          user_all: (fresh as any).user_all ?? false,

          vip: toNumericSqlIds(fresh.vip || []),
          vip_mode: (fresh as any).vip_mode ?? "DISABLED",
          vip_all: (fresh as any).vip_all ?? false,

          is_active: !fresh.is_active,
        },
        token,
      };

      const response = await ActivityNotAllowedICDAxiosInstance.put(
        "updateActivityNotAllowedWithIcd",
        payload,
      );

      if (response.data.success) {
        await fetchItems();
        toast({
          title: "Success",
          description: `Status changed to ${!fresh.is_active ? "Active" : "Inactive"}.`,
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data.error ||
            response.data.message ||
            "Failed to update status.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.message ||
          "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  const hasActivitySelection = (): boolean =>
    Object.values(activitiesByType).some((items) => items.length > 0) ||
    ActivityMedicalGroups.length > 0 ||
    ActivityCustomGroups.length > 0;

  const hasIcdSelection = (): boolean =>
    icd.length > 0 ||
    icdMedicalGroups.length > 0 ||
    icdCustomGroups.length > 0;

  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    if (!hasActivitySelection()) { toast({ title: "Validation Error", description: "Please select at least one Activity Code, Medical Group, or Custom Group.", variant: "destructive" }); return; }
    if (!hasIcdSelection()) { toast({ title: "Validation Error", description: "Please select at least one ICD Code, Medical ICD Group, or Custom ICD Group.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const payload = { data: [buildPayload()], token };
      const response = await ActivityNotAllowedICDAxiosInstance.post("addNewActivityNotAllowedWithIcd", payload);
      if (response.data.success) {
        await fetchItems(); setCurrentPage(0); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activity Not Allowed ICD Added Successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error).join("\n") || response.data.message || "Failed to add.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    if (!hasActivitySelection()) { toast({ title: "Validation Error", description: "Please select at least one Activity Code, Medical Group, or Custom Group.", variant: "destructive" }); return; }
    if (!hasIcdSelection()) { toast({ title: "Validation Error", description: "Please select at least one ICD Code, Medical ICD Group, or Custom ICD Group.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }
    try {
      const response = await ActivityNotAllowedICDAxiosInstance.put(
        `updateActivityNotAllowedWithIcd`, { data: { ...buildPayload(), id }, token }
      );
      if (response.data.success) {
        await fetchItems(); clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Activity Not Allowed ICD updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Datum) => {
    setClearSpecialty(false); setClearProvider(false); setClearDoctor(false);
    setClearNetworks(false); setClearInsuranceplan(false); setClearPatient(false);
    setClearPolicy(false); setClearVip(false); setClearIcd(false);
    setClearIcdMedicalGroups(false); setClearIcdCustomGroups(false);
    setClearActivityMedicalGroups(false); setClearActivityCustomGroups(false);

    setRelationName(item.relation_name);
    setIsActive(item.is_active);
    setEditIndex(item.id);

    const actCodes: string[] = item.activity || [];
    const actTypes: string[] = Array.isArray(item.activity_type)
      ? item.activity_type : actCodes.map(() => (item.activity_type as string) || "cpt");
    const flat: CodeWithType[] = actCodes.map((code, idx) => ({
      code, type: TYPE_MAP[(actTypes[idx] || "cpt").toLowerCase()] || "CPT",
    }));

    if (flat.length > 0) { setActivitiesByType(groupByType(flat)); setSelectedMainOption("list_of_Activity_code"); }
    else { setActivitiesByType({ ...EMPTY_BY_TYPE }); setSelectedMainOption(null); }

    if (item.activity_medicalgroups?.length > 0) {
      setActivityMedicalGroups(item.activity_medicalgroups.map((o: any) =>
        typeof o === "string"
          ? { value: o, label: o }
          : { value: o.id ?? o.value ?? o.name, label: o.name ?? o.label ?? String(o.id ?? o.value ?? "") }
      ));
      if (!flat.length) setSelectedMainOption("medical_Activity_groups");
    } else { setActivityMedicalGroups([]); }

    if (item.activity_customgroups?.length > 0) {
      setActivityCustomGroups(item.activity_customgroups.map((o: any) =>
        typeof o === "string"
          ? { value: o, label: o }
          : { value: o.id ?? o.value ?? o.name, label: o.name ?? o.label ?? String(o.id ?? o.value ?? "") }
      ));
      if (!flat.length && !item.activity_medicalgroups?.length) setSelectedMainOption("custom_Activity_groups");
    } else { setActivityCustomGroups([]); }

    if (item.icd?.length > 0) { setIcd(item.icd.map((v: any) => ({ value: v, label: v.toString() }))); setSelectedAllowedOption("list_of_icd"); }
    else { setIcd([]); setSelectedAllowedOption(null); }

    if (item.icd_medicalgroups?.length > 0) {
      setIcdMedicalGroups(item.icd_medicalgroups.map((o: any) =>
        typeof o === "string"
          ? { value: o, label: o }
          : { value: o.id ?? o.value ?? o.name, label: o.name ?? o.label ?? String(o.id ?? o.value ?? "") }
      ));
      if (!item.icd?.length) setSelectedAllowedOption("medical_icd_groups");
    } else { setIcdMedicalGroups([]); }

    if (item.icd_customgroups?.length > 0) {
      setIcdCustomGroups(item.icd_customgroups.map((o: any) =>
        typeof o === "string"
          ? { value: o, label: o }
          : { value: o.id ?? o.value ?? o.name, label: o.name ?? o.label ?? String(o.id ?? o.value ?? "") }
      ));
      if (!item.icd?.length && !item.icd_medicalgroups?.length) setSelectedAllowedOption("custom_icd_groups");
    } else { setIcdCustomGroups([]); }

    const mappedNetworks = (item.networks || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id,
            label: formatSqlEntityLabel(o, o.Network_name, o.network_name, o.name),
          }
        : { value: o, label: String(o) },
    );
    setNetworks(mappedNetworks);
    setNetworkMode(modeFromString(item.network_mode));
    setIsNetworkActive(modeFromString(item.network_mode) !== null);
    setSelectAllNetworks(item.network_all ?? false);

    const mappedInsurance = (item.insuranceplan || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id,
            label: formatSqlEntityLabel(o, o.Plan_name, o.plan_name, o.name),
          }
        : { value: o, label: String(o) },
    );
    setInsuranceplan(mappedInsurance);
    setInsuranceMode(modeFromString(item.insuranceplan_mode));
    setIsInsuranceActive(modeFromString(item.insuranceplan_mode) !== null);
    setSelectAllInsurance(item.insuranceplan_all ?? false);

    const mappedProvider = (item.provider || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id ?? o.Provider_id,
            label: `${o.Provider_id ?? o.provider_id ?? o.id ? `(${o.Provider_id ?? o.provider_id ?? o.id}) ` : ""}${o.Provider_name ?? o.provider_name ?? o.providerName ?? o.name ?? ""}`.trim(),
            dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
            providerId: o.Provider_id ?? o.provider_id ?? "",
            providerName: o.Provider_name ?? o.provider_name ?? o.providerName ?? o.name ?? "",
          }
        : { value: o, label: String(o) },
    );
    setProvider(mappedProvider);
    setProviderMode(modeFromString(item.provider_mode));
    setIsProviderActive(modeFromString(item.provider_mode) !== null);
    setSelectAllProviders(item.provider_all ?? false);

    const mappedDoctor = (item.doctor || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id ?? o.Doctor_id,
            label: `${o.Doctor_id ?? o.doctor_id ?? o.id ? `(${o.Doctor_id ?? o.doctor_id ?? o.id}) ` : ""}${o.Doctor_name ?? o.doctor_name ?? o.doctorName ?? o.name ?? ""}`.trim(),
            dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
            doctorId: o.Doctor_id ?? o.doctor_id ?? "",
            Doctor_id: o.Doctor_id ?? o.doctor_id ?? "",
            doctorName: o.Doctor_name ?? o.doctor_name ?? o.doctorName ?? o.name ?? "",
            Doctor_name: o.Doctor_name ?? o.doctor_name ?? o.doctorName ?? o.name ?? "",
          }
        : { value: o, label: String(o) },
    );
    setDoctor(mappedDoctor);
    setDoctorMode(modeFromString(item.doctor_mode));
    setIsDoctorActive(modeFromString(item.doctor_mode) !== null);
    setSelectAllDoctor(item.doctor_all ?? false);

    const patientSource = (item as any).patients ?? (item as any).patient ?? [];
    const mappedPatients = (Array.isArray(patientSource) ? patientSource : []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id,
            label: formatPatientLabel(o),
            dbId: Number(o.id ?? o.dbId ?? o.db_id ?? 0) || undefined,
            patientName: o.full_name ?? o.Full_name ?? o.name ?? "",
            nationalId: o.National_id ?? o.national_id ?? "",
            memberId: o.Member_id ?? o.member_id ?? "",
            patientPassport: o.Patient_passport ?? o.patient_passport ?? o.passport ?? "",
          }
        : { value: o, label: String(o) },
    );
    setPatients(mappedPatients);
    setPatientMode(modeFromString(item.patient_mode));
    setIsPatientActive(modeFromString(item.patient_mode) !== null);
    setSelectAllPatients(item.patient_all ?? false);

    const mappedVip = (item.vip || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id,
            label: formatSqlEntityLabel(o, o.name, o.Vip_name, o.vip_name),
          }
        : { value: o, label: String(o) },
    );
    setVip(mappedVip);
    setVipMode(modeFromString(item.vip_mode));
    setIsVipActive(modeFromString(item.vip_mode) !== null);
    setSelectAllVip(item.vip_all ?? false);

    const mappedPolicy = Array.isArray(item.policy)
      ? item.policy.map((obj: any) =>
          typeof obj === "string"
            ? { id: 0, Policy_id: obj, Policy_name: obj, plans: [] }
            : {
                ...entityActivity(obj),
                id: obj.id || 0,
                Policy_id: obj.Policy_id ?? obj.policy_id ?? "",
                Policy_name: obj.Policy_name ?? obj.policy_name ?? obj.name ?? "",
                plans: obj.plans || [],
              },
        )
      : [];
    setPolicy(mappedPolicy);
    setPolicyMode(modeFromString(item.policy_mode));
    setIsPolicyActive(modeFromString(item.policy_mode) !== null);
    setSelectAllPolicy(item.policy_all ?? false);

    setPayer(item.payer || []);
    setPayerMode(modeFromString(item.payer_mode));
    setIsPayerActive(modeFromString(item.payer_mode) !== null);
    setSelectAllPayer(item.payer_all ?? false);

    setUserLevel(item.user_level || []);
    setUserMode(modeFromString(item.user_mode));
    setIsUserActive(modeFromString(item.user_mode) !== null);
    setSelectAllUser(item.user_all ?? false);

    const mappedSpecialty = (item.specialty || []).map((o: any) =>
      typeof o === "object" && o !== null
        ? {
            ...entityActivity(o),
            value: o.id ?? o.dbId ?? o.db_id,
            label: `${o.speciality_code ?? o.specialty_code ?? o.id ? `(${o.speciality_code ?? o.specialty_code ?? o.id}) ` : ""}${o.name ?? o.Specialty_name ?? o.specialty_name ?? o.speciality_name ?? ""}`.trim(),
          }
        : { value: o, label: String(o) },
    );
    setSpecialty(mappedSpecialty);
    setSpecialtyMode(modeFromString(item.specialty_mode));
    setIsSpecialtyActive(modeFromString(item.specialty_mode) !== null);
    setSelectAllSpecialty(item.specialty_all ?? false);

    const hasEntities = !!(
      mappedProvider.length ||
      mappedDoctor.length ||
      mappedNetworks.length ||
      mappedInsurance.length ||
      mappedPolicy.length ||
      mappedPatients.length ||
      mappedVip.length ||
      (item.payer || []).length > 0 ||
      mappedSpecialty.length ||
      (item.user_level || []).length > 0 ||
      item.provider_all ||
      item.doctor_all ||
      item.network_all ||
      item.insuranceplan_all ||
      item.policy_all ||
      item.patient_all ||
      item.vip_all ||
      item.payer_all ||
      item.user_all ||
      item.specialty_all ||
      modeFromString(item.provider_mode) !== null ||
      modeFromString(item.doctor_mode) !== null ||
      modeFromString(item.network_mode) !== null ||
      modeFromString(item.insuranceplan_mode) !== null ||
      modeFromString(item.policy_mode) !== null ||
      modeFromString(item.patient_mode) !== null ||
      modeFromString(item.vip_mode) !== null ||
      modeFromString(item.payer_mode) !== null ||
      modeFromString(item.user_mode) !== null ||
      modeFromString(item.specialty_mode) !== null
    );
    setShowEntities(hasEntities);
  };

  const handleDownload = (item: Datum) => {
    const flat: CodeWithType[] = (item as any).activity_flat || [];
    const rows = ["relation_name;activity_code;activity_type;is_active"];
    rows.push([item.relation_name || "", flat.map(c => c.code).join(","), flat.map(c => c.type).join(","), item.is_active ? "TRUE" : "FALSE"].join(";"));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ActivityNotAllowedICD_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
     cell: ({ row }) => (
      <div className="text-center w-full font-medium">
        {currentPage * pageSize + row.index + 1}
      </div>
    ),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {row.getValue("relation_name") as string}
          </div>
        );
      },
    },
    {
      accessorKey: "activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const flat: CodeWithType[] = (item as any).activity_flat || [];
        const custom  = Array.isArray(item.activity_customgroups)  ? item.activity_customgroups.map((g: any)  => g.name || g) : [];
        const medical = Array.isArray(item.activity_medicalgroups) ? item.activity_medicalgroups.map((g: any) => g.name || g) : [];
        if (flat.length === 0 && custom.length === 0 && medical.length === 0) return <div className="w-full text-center text-gray-400">—</div>;
        return (
          <ActivityCell
            items={flat}
            medicalGroups={medical}
            customGroups={custom}
            searchTerm={filterSearch}
            onShowAll={(its, med, cus) => { setAllCodesItems(its); setAllCodesMedical(med); setAllCodesCustom(cus); setAllCodesTitle(`${item.relation_name} – Activity Codes`); setAllCodesOpen(true); }}
          />
        );
      },
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        const icdCodes = Array.isArray(item.icd) ? item.icd : [];
        const icdCustom = Array.isArray(item.icd_customgroups)
          ? item.icd_customgroups.map((group: any) => group.name || group)
          : [];
        const icdMedical = Array.isArray(item.icd_medicalgroups)
          ? item.icd_medicalgroups.map((group: any) => group.name || group)
          : [];

        const allDisplay = [
          ...icdCodes.map((code: string) => ({
            label: String(code),
            kind: "icd" as const,
          })),
          ...icdMedical.map((group: string) => ({
            label: String(group),
            kind: "medical" as const,
          })),
          ...icdCustom.map((group: string) => ({
            label: String(group),
            kind: "custom" as const,
          })),
        ];

        const total = allDisplay.length;

        if (total === 0) {
          return <div className="w-full text-center text-gray-400">—</div>;
        }

        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingDisplay = hasSearch
          ? allDisplay.filter((entry) =>
              entry.label.toLowerCase().includes(searchTerm),
            )
          : [];

        const hasMatches = hasSearch && matchingDisplay.length > 0;
        const displaySource = hasMatches ? matchingDisplay : allDisplay;
        const shown = displaySource.slice(0, 3);
        const remainingCount = Math.max(0, displaySource.length - shown.length);

        const kindStyle = (
          kind: "icd" | "medical" | "custom",
          isMatching: boolean,
        ) => {
          if (isMatching) {
            return {
              bg: "#fef08a",
              color: "#713f12",
              border: "#facc15",
              tag:
                kind === "icd"
                  ? "ICD"
                  : kind === "medical"
                    ? "MED"
                    : "CUS",
            };
          }

          if (kind === "icd") {
            return {
              bg: "#011934",
              color: "#fff",
              border: "#011934",
              tag: "ICD",
            };
          }

          if (kind === "medical") {
            return {
              bg: "#f0fdf4",
              color: "#15803d",
              border: "#86efac",
              tag: "MED",
            };
          }

          return {
            bg: "#faf5ff",
            color: "#7e22ce",
            border: "#d8b4fe",
            tag: "CUS",
          };
        };

        return (
          <div className="w-full px-2 py-1.5">
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {shown.map((entry, index) => {
                const isMatching =
                  hasSearch &&
                  entry.label.toLowerCase().includes(searchTerm);
                const style = kindStyle(entry.kind, isMatching);

                return (
                  <div
                    key={`${entry.kind}-${entry.label}-${index}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 4px 2px 8px",
                      borderRadius: 6,
                      background: style.bg,
                      border: `1px solid ${style.border}`,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: style.color,
                      }}
                    >
                      {entry.label}
                    </span>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.2)",
                        color: style.color,
                        opacity: 0.85,
                        border: `1px solid ${style.border}`,
                      }}
                    >
                      {style.tag}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 pt-1">
              <span className="text-xs font-bold text-gray-500">
                {hasMatches
                  ? `${matchingDisplay.length}/${allDisplay.length}`
                  : total}
              </span>

              {remainingCount > 0 && (
                <button
                  onClick={() => {
                    setIcdCodesDialogList(
                      hasMatches ? matchingDisplay : allDisplay,
                    );
                    setIcdCodesDialogTitle(
                      `${item.relation_name} – ICD Codes`,
                    );
                    setIcdCodesDialogOpen(true);
                  }}
                  className={`text-xs font-semibold ${
                    hasMatches
                      ? "text-yellow-600 hover:text-yellow-800"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  +{remainingCount} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <div className="w-full text-center">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => { void handleOpenDetails(row.original); }} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="activity-not-allowed-icd">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-ban" />
          </span>
          Activity Not Allowed with ICD
        </h5>
      </div>

      <DataTable2
        qaIdPrefix="activity-not-allowed-icd-data-table"
        columns={columns}
        data={items.data}
        searchColumns={[]}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onEdit={(item) => { void handleEditClick(item); }}
        onDelete={(id) => {
          const item = items.data.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={() => { setEditIndex(null); clear(); setIsDialogOpen(true); }}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width:"100%", position:"relative" }}>
<div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", padding:"10px 160px 10px 16px", background:"linear-gradient(135deg,#f8fafc 0%,#f1f5f9 100%)", borderRadius:12, border:"1px solid #e2e8f0", boxShadow:"0 1px 3px rgba(0,0,0,0.04)", marginBottom:12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(true); }}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 16px", fontSize:13, fontWeight:600, color:"#ffffff", background:"#1e3a8a", border:"none", borderRadius:8, cursor:"pointer", whiteSpace:"nowrap", boxShadow:"0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add New Relation
              </button>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Activity, ICD, Medical Group, or Custom Group."
              />
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>Status</span>
                <div style={{ display:"flex", alignItems:"center", padding:3, background:"#e2e8f0", borderRadius:9999 }}>
                  {[{ value:"", label:"All" }, { value:"true", label:"Active" }, { value:"false", label:"Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)}
                      style={{ padding:"5px 11px", fontSize:12, fontWeight:500, borderRadius:9999, border:"none", cursor:"pointer", whiteSpace:"nowrap", background:filterStatus===option.value ? option.value==="true" ? "#10b981" : option.value==="false" ? "#94a3b8" : "#ffffff" : "transparent", color:filterStatus===option.value ? option.value==="" ? "#334155" : "#ffffff" : "#64748b", boxShadow:filterStatus===option.value?"0 2px 4px rgba(0,0,0,0.1)":"none" }}>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ position:"absolute", top:"50%", right:16, transform:"translateY(-50%)" }}>
                <FileUpload fetchItems={fetchItems} />
              </div>
            </div>
          </div>
        }
      />

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) setSelectedItem(null); }}>
        <DialogContent className="max-w-[960px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl"
          style={{ boxShadow:"0 25px 60px rgba(0,0,0,0.18),0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#2563eb 100%)", padding:"24px 28px 20px", flexShrink:0, position:"relative", overflow:"hidden" }}>
                <div style={{ position:"absolute", top:-40, right:-40, width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.04)" }} />
                <div style={{ display:"flex", alignItems:"flex-start", gap:16, position:"relative", zIndex:1 }}>
                  <div style={{ width:48, height:48, borderRadius:14, background:"rgba(255,255,255,0.12)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:"#fff", flexShrink:0, border:"1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-ban" />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:2 }}>
                      <h2 style={{ fontSize:20, fontWeight:700, color:"#fff", margin:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{selectedItem.relation_name}</h2>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 12px", borderRadius:9999, fontSize:11, fontWeight:600, flexShrink:0, background:selectedItem.is_active?"rgba(34,197,94,0.2)":"rgba(239,68,68,0.15)", color:selectedItem.is_active?"#86efac":"#fca5a5", border:`1px solid ${selectedItem.is_active?"rgba(34,197,94,0.25)":"rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:"currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 12px", borderRadius:9999, fontSize:11, fontWeight:600, flexShrink:0, background:"rgba(239,68,68,0.15)", color:"#fca5a5", border:"1px solid rgba(239,68,68,0.2)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize:10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize:12, color:"rgba(147,197,253,0.8)", margin:0 }}>Rule ID: <span style={{ fontWeight:600, color:"#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
              </div>

              <div style={{ overflowY:"auto", flex:1, background:"#f8fafc", padding:"6px 0 16px" }}>
                {/* Activity & ICD */}
                <div style={{ padding:"20px 28px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#eff6ff", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #dbeafe" }}>
                      <i className="fa-solid fa-code-branch" style={{ fontSize:12, color:"#3b82f6" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Activity & ICD Configuration</span>
                  </div>
                  {(() => {
                    const typeColors: Record<string, { bg:string; color:string; border:string; dot:string }> = {
                      CPT:    { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe", dot:"#3b82f6" },
                      HCPCS:  { bg:"#f0fdf4", color:"#15803d", border:"#bbf7d0", dot:"#22c55e" },
                      Dental: { bg:"#faf5ff", color:"#7e22ce", border:"#e9d5ff", dot:"#a855f7" },
                      Drugs:  { bg:"#fff7ed", color:"#c2410c", border:"#fed7aa", dot:"#f97316" },
                    };
                    const flat: CodeWithType[] = (selectedItem as any).activity_flat || [];
                    const grouped: Record<string, string[]> = { CPT:[], HCPCS:[], Dental:[], Drugs:[] };
                    flat.forEach(({ code, type }) => { if (grouped[type]) grouped[type].push(String(code)); });
                    const medGroups    = selectedItem.activity_medicalgroups || [];
                    const customGroups = selectedItem.activity_customgroups  || [];
                    const icdCodes     = selectedItem.icd                    || [];
                    const icdMed       = selectedItem.icd_medicalgroups      || [];
                    const icdCustom    = selectedItem.icd_customgroups       || [];
                    return (
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                        <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:14, paddingBottom:12, borderBottom:"1px solid #f1f5f9" }}>
                            <span style={{ fontSize:12, fontWeight:700, color:"#1e293b" }}>Activity Codes</span>
                            <span style={{ marginLeft:"auto", fontSize:11, color:"#94a3b8" }}>{flat.length} codes</span>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {flat.length > 0 && (
                              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                                {Object.entries(grouped).map(([type, codes]) => {
                                  if (codes.length === 0) return null;
                                  const tc = typeColors[type];
                                  return (
                                    <div key={type} style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"12px 14px" }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                                        <span style={{ width:6, height:6, borderRadius:2, background:tc.dot, flexShrink:0 }} />
                                        <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{type}</span>
                                      </div>
                                      <div style={{ display:"flex", flexWrap:"wrap" as const, gap:4 }}>
                                        {codes.map((code, idx) => (<span key={idx} style={{ padding:"2px 8px", borderRadius:9999, fontSize:11, fontWeight:500, background:tc.bg, color:tc.color, border:`0.5px solid ${tc.border}` }}>{code}</span>))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {[
                              { label:"Medical Groups", items:(medGroups as any[]).map(g=>g.name||g),    tagBg:"#f0fdf4", tagColor:"#15803d", tagBorder:"#bbf7d0" },
                              { label:"Custom Groups",  items:(customGroups as any[]).map(g=>g.name||g), tagBg:"#faf5ff", tagColor:"#7e22ce", tagBorder:"#e9d5ff" },
                            ].map((section) => (
                              <div key={section.label} style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"10px 12px" }}>
                                <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{section.label}</span>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5, marginTop:6 }}>
                                  {section.items.length > 0
                                    ? section.items.map((it, i) => <span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:section.tagBg, color:section.tagColor, border:`0.5px solid ${section.tagBorder}` }}>{it}</span>)
                                    : <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>None</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ background:"#fff", borderRadius:14, border:"1px solid #e2e8f0", padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:14, paddingBottom:12, borderBottom:"1px solid #f1f5f9" }}>
                            <span style={{ fontSize:12, fontWeight:700, color:"#1e293b" }}>ICD Codes</span>
                            <span style={{ marginLeft:"auto", fontSize:11, color:"#94a3b8" }}>{icdCodes.length+(icdMed as any[]).length+(icdCustom as any[]).length} total</span>
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                            {icdCodes.length > 0 && (
                              <div style={{ background:"#fff1f2", borderRadius:10, border:"1px solid #fecdd3", padding:"10px 12px" }}>
                                <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>ICD Codes</span>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5, marginTop:6 }}>
                                  {icdCodes.map((c: any, i: number) => (<span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:"#ffe4e6", color:"#be123c", border:"0.5px solid #fecdd3" }}>{c}</span>))}
                                </div>
                              </div>
                            )}
                            {[
                              { label:"Medical ICD Groups", items:(icdMed    as any[]).map(g=>g.name||g), tagBg:"#f0fdf4", tagColor:"#15803d", tagBorder:"#bbf7d0" },
                              { label:"Custom ICD Groups",  items:(icdCustom as any[]).map(g=>g.name||g), tagBg:"#faf5ff", tagColor:"#7e22ce", tagBorder:"#e9d5ff" },
                            ].map((section) => (
                              <div key={section.label} style={{ background:"#f8fafc", borderRadius:10, border:"1px solid #e2e8f0", padding:"10px 12px" }}>
                                <span style={{ fontSize:11, fontWeight:600, color:"#64748b" }}>{section.label}</span>
                                <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5, marginTop:6 }}>
                                  {section.items.length > 0
                                    ? section.items.map((it, i) => <span key={i} style={{ padding:"3px 10px", borderRadius:9999, fontSize:11, fontWeight:500, background:section.tagBg, color:section.tagColor, border:`0.5px solid ${section.tagBorder}` }}>{it}</span>)
                                    : <span style={{ color:"#94a3b8", fontSize:12, fontStyle:"italic" }}>None</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ height:1, background:"#e2e8f0", margin:"0 28px" }} />

                {/* Target Scope */}
                <div style={{ padding:"20px 28px 16px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#fef3c7", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #fde68a" }}>
                      <i className="fa-solid fa-bullseye" style={{ fontSize:12, color:"#d97706" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Target Scope & Visibility</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
            {[
  { label:"Providers",      icon:"fa-hospital",         color:"#3b82f6", mode: selectedItem.provider_mode,      items:(selectedItem.provider||[]).map((p:any)=>linkedDisplay(`${p?.Provider_id ?? p?.provider_id ?? p?.id ? `(${p?.Provider_id ?? p?.provider_id ?? p?.id}) ` : ""}${p?.Provider_name ?? p?.provider_name ?? p?.providerName ?? p?.name ?? ""}`.trim(), p)).filter(Boolean), exclude:selectedItem.apply_to_other_providers,     selectAll:selectedItem.select_all_providers },
  { label:"Doctors",        icon:"fa-stethoscope",      color:"#06b6d4", mode: selectedItem.doctor_mode,         items:(selectedItem.doctor||[]).map((d:any)=>linkedDisplay(`${d?.Doctor_id ?? d?.doctor_id ?? d?.id ? `(${d?.Doctor_id ?? d?.doctor_id ?? d?.id}) ` : ""}${d?.Doctor_name ?? d?.doctor_name ?? d?.doctorName ?? d?.name ?? ""}`.trim(), d)).filter(Boolean), exclude:selectedItem.apply_to_other_doctor,        selectAll:selectedItem.select_all_doctor },
  { label:"Specialty",      icon:"fa-user-doctor",      color:"#0ea5e9", mode: selectedItem.specialty_mode,      items:(selectedItem.specialty||[]).map((s:any)=>linkedDisplay(`${s?.speciality_code ?? s?.specialty_code ?? s?.id ? `(${s?.speciality_code ?? s?.specialty_code ?? s?.id}) ` : ""}${s?.name ?? s?.Specialty_name ?? s?.specialty_name ?? s?.speciality_name ?? ""}`.trim(), s)).filter(Boolean), exclude:selectedItem.apply_to_other_specialty,     selectAll:selectedItem.select_all_specialty },
  { label:"Networks",       icon:"fa-network-wired",    color:"#8b5cf6", mode: selectedItem.network_mode,        items:(selectedItem.networks||[]).map((n:any)=>linkedDisplay(formatSqlEntityLabel(n,n?.Network_name,n?.network_name,n?.name), n)).filter(Boolean), exclude:selectedItem.apply_to_other_networks,      selectAll:selectedItem.select_all_networks },
  { label:"Insurance Plans",icon:"fa-shield-halved",    color:"#f59e0b", mode: selectedItem.insuranceplan_mode,  items:(selectedItem.insuranceplan||[]).map((i:any)=>linkedDisplay(formatSqlEntityLabel(i,i?.Plan_name,i?.plan_name,i?.name), i)).filter(Boolean), exclude:selectedItem.apply_to_other_insuranceplan, selectAll:selectedItem.select_all_insuranceplan },
  { label:"Policies",       icon:"fa-file-lines",       color:"#10b981", mode: selectedItem.policy_mode,         items:(selectedItem.policy||[]).map((p:any)=>{ const planNames=(Array.isArray(p?.plans)?p.plans:[]).map((pl:any)=>pl?.Plan_name||pl?.plan_name||pl?.name||"").filter(Boolean).join(", "); const displayId=p?.Policy_id ?? p?.policy_id ?? p?.id; const name=p?.Policy_name ?? p?.policy_name ?? p?.name ?? ""; const base=displayId!=null && String(displayId)!=="" ? `(${displayId}) ${name}`.trim() : name; return linkedDisplay(`${base}${planNames ? ` (${planNames})` : ""}`, p); }).filter(Boolean), exclude:selectedItem.apply_to_other_policy,  selectAll:selectedItem.select_all_policy },
  { label:"Patients",       icon:"fa-users",            color:"#ec4899", mode: selectedItem.patient_mode,        items:(selectedItem.patients||[]).map((p:any)=>linkedDisplay(formatPatientLabel(p), p)).filter(Boolean), exclude:selectedItem.apply_to_other_patients,      selectAll:selectedItem.select_all_patients },
  { label:"VIP",            icon:"fa-star",             color:"#eab308", mode: selectedItem.vip_mode,            items:(selectedItem.vip||[]).map((v:any)=>linkedDisplay(formatSqlEntityLabel(v,v?.name,v?.Vip_name,v?.vip_name), v)).filter(Boolean), exclude:selectedItem.apply_to_vip,                 selectAll:selectedItem.select_all_vip },
  { label:"Payer",          icon:"fa-building-columns", color:"#6366f1", mode: selectedItem.payer_mode,          items:(selectedItem.payer||[]).map((p:any)=>String(p)),                          exclude:selectedItem.apply_to_other_payer,         selectAll:selectedItem.select_all_payer },
  { label:"User Levels",    icon:"fa-user-gear",        color:"#64748b", mode: selectedItem.user_mode,           items:(selectedItem.user_level||[]).map((u:any)=>String(u)),                     exclude:selectedItem.apply_to_other_user,          selectAll:selectedItem.select_all_user },
].map((e) => (
  <div key={e.label} style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px", boxShadow:"0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
        <div style={{ width:24, height:24, borderRadius:6, background:`${e.color}12`, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <i className={`fa-solid ${e.icon}`} style={{ fontSize:10, color:e.color }} />
        </div>
        <span style={{ fontSize:12, fontWeight:600, color:"#334155" }}>{e.label}</span>
      </div>
      {e.mode !== "DISABLED" && (
        <span style={{ fontSize:10, padding:"2px 8px", borderRadius:9999, fontWeight:600,
          background: e.exclude ? "#fef2f2" : "#f0fdf4",
          color:      e.exclude ? "#dc2626" : "#15803d",
          border:     `1px solid ${e.exclude ? "#fecaca" : "#bbf7d0"}` }}>
          {e.exclude ? "Exclusion" : "Inclusion"}
        </span>
      )}
    </div>
    {e.selectAll
      ? <span style={{ fontSize:11, padding:"3px 10px", borderRadius:9999, background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", fontWeight:600 }}>All Selected</span>
      : e.items.length > 0
        ? <div style={{ display:"flex", flexWrap:"wrap" as const, gap:5 }}>{e.items.map((it:string, i:number) => {
            const inactive = it.endsWith(" (Inactive)");
            return <span key={i} style={{ fontSize:11, padding:"3px 10px", borderRadius:9999,
              background: inactive ? "#d1d5db" : "#f1f5f9",
              color: inactive ? "#334155" : "#334155",
              border: inactive ? "1px solid #94a3b8" : "1px solid #e2e8f0",
              fontWeight: inactive ? 600 : 500 }}>{it}</span>;
          })}</div>
        : <span style={{ color:"#cbd5e1", fontSize:12, fontStyle:"italic" }}>No items</span>
    }
  </div>
))}
                  </div>
                </div>

                <div style={{ height:1, background:"#e2e8f0", margin:"0 28px" }} />

                {/* Audit Trail */}
                <div style={{ padding:"20px 28px 8px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16 }}>
                    <div style={{ width:28, height:28, borderRadius:8, background:"#f0fdf4", display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize:12, color:"#16a34a" }} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Created By</span>
                      <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:10 }}>
                        <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#3b82f6,#2563eb)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#fff" }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Created On</span>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize:14, color:"#3b82f6" }} />
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{(selectedItem as any).createdat ? formatDisplayDateTime((selectedItem as any).createdat) : "—"}</span>
                      </div>
                    </div>
                    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #e2e8f0", padding:"14px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:600, color:"#94a3b8" }}>Last Updated</span>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize:14, color:"#10b981" }} />
                        <span style={{ fontSize:13, fontWeight:600, color:"#1e293b" }}>{(selectedItem as any).updatedat ? formatDisplayDateTime((selectedItem as any).updatedat) : "—"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

           
              {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; user_id?: number; at?: string; date?: string; [key: string]: any }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => {
        const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
        return (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id ?? entry.user_id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id ?? entry.user_id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {updateDate ? formatDisplayDateTime(updateDate) : "—"}
          </span>
        </div>
      );})}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
   <div style={{ padding:"14px 28px", borderTop:"1px solid #e2e8f0", background:"#fff", display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, flexShrink:0 }}>
                <Button onClick={() => setSelectedItem(null)} style={{ background:"linear-gradient(135deg,#1e3a8a,#2563eb)", color:"#fff", fontWeight:600, padding:"8px 28px", borderRadius:10 }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

 {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activity Not Allowed with ICD" : "Edit Activity Not Allowed with ICD"}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic Information" subtitle="Name and status of the relation">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={(e) => setRelationName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity & ICD configuration" subtitle="Choose one source for each side">
              <div className="space-y-5">
                {/* Activity Side */}
                <SourcePicker
                  title="Activity *"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  options={[
                    { key: "list_of_Activity_code", label: "Activity Codes", icon: "fa-code", count: activitiesByType.CPT.length + activitiesByType.HCPCS.length + activitiesByType.Dental.length + activitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: ActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: ActivityCustomGroups.length },
                  ]}
                >
                  {selectedMainOption === "list_of_Activity_code" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({activitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={activitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMainOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={setActivityMedicalGroups} defaultValue={ActivityMedicalGroups} clear={clearActivityMedicalGroups} />
                  )}
                  {selectedMainOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={setActivityCustomGroups} defaultValue={ActivityCustomGroups} clear={clearActivityCustomGroups} />
                  )}
                </SourcePicker>

                {/* ICD Side */}
                <SourcePicker
                  title="ICD *"
                  selected={selectedAllowedOption}
                  onSelect={setSelectedAllowedOption}
                  options={[
                    { key: "list_of_icd", label: "List of ICD", icon: "fa-file-medical", count: icd.length },
                    { key: "medical_icd_groups", label: "Medical Groups", icon: "fa-layer-group", count: icdMedicalGroups.length },
                    { key: "custom_icd_groups", label: "Custom Groups", icon: "fa-cubes", count: icdCustomGroups.length },
                  ]}
                >
                  {selectedAllowedOption === "list_of_icd" && (
                    <Listofmedicalcode onChange={setIcd} description={IcdDescription} defaultValue={icd} clear={clearIcd} />
                  )}
                  {selectedAllowedOption === "medical_icd_groups" && (
                    <ListofMedicalGroup onChange={setIcdMedicalGroups} defaultValue={icdMedicalGroups} clear={clearIcdMedicalGroups} />
                  )}
                  {selectedAllowedOption === "custom_icd_groups" && (
                    <ListofCustomIcd onChange={setIcdCustomGroups} defaultValue={icdCustomGroups} clear={clearIcdCustomGroups} />
                  )}
                </SourcePicker>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                    <p className="text-xs text-blue-600">Scope and visibility — who this rule applies to</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {showEntities && (
                <div className="p-5 bg-blue-50">
                  <div className="flex gap-3 p-3 bg-blue-100 border-l-4 border-blue-500 rounded-r-lg mb-4">
                    <svg className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <p className="text-xs text-blue-900 leading-relaxed">
                      <span className="font-semibold text-emerald-700">Include</span> = apply only to selected items &nbsp;·&nbsp;
                      <span className="font-semibold text-red-700">Exclude</span> = apply to everyone except selected items
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if(providerMode==="EXCLUSION"){setProvider([]);setClearProvider(true);setSelectAllProviders(false);setProviderMode(null);setIsProviderActive(false);}else{setProvider([]);setClearProvider(true);setSelectAllProviders(false);setProviderMode("EXCLUSION");setIsProviderActive(true);} }}
                      onInclusion={() => { if(providerMode==="INCLUSION"){setProvider([]);setClearProvider(true);setSelectAllProviders(false);setProviderMode(null);setIsProviderActive(false);}else{setProvider([]);setClearProvider(true);setSelectAllProviders(false);setProviderMode("INCLUSION");setIsProviderActive(true);} }}
                      onClear={() => { setProvider([]);setProviderMode(null);setIsProviderActive(false);setClearProvider(true);setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllProviders(c); if(c){setProvider([]);setClearProvider(true);} }); }} className="w-4 h-4 accent-blue-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <ListOfProviderName onChange={setProvider} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if(doctorMode==="EXCLUSION"){setDoctor([]);setClearDoctor(true);setSelectAllDoctor(false);setDoctorMode(null);setIsDoctorActive(false);}else{setDoctor([]);setClearDoctor(true);setSelectAllDoctor(false);setDoctorMode("EXCLUSION");setIsDoctorActive(true);} }}
                      onInclusion={() => { if(doctorMode==="INCLUSION"){setDoctor([]);setClearDoctor(true);setSelectAllDoctor(false);setDoctorMode(null);setIsDoctorActive(false);}else{setDoctor([]);setClearDoctor(true);setSelectAllDoctor(false);setDoctorMode("INCLUSION");setIsDoctorActive(true);} }}
                      onClear={() => { setDoctor([]);setDoctorMode(null);setIsDoctorActive(false);setClearDoctor(true);setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllDoctor(c); if(c){setDoctor([]);setClearDoctor(true);} }); }} className="w-4 h-4 accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={setDoctor} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if(networkMode==="EXCLUSION"){setNetworks([]);setClearNetworks(true);setSelectAllNetworks(false);setNetworkMode(null);setIsNetworkActive(false);}else{setNetworks([]);setClearNetworks(true);setSelectAllNetworks(false);setNetworkMode("EXCLUSION");setIsNetworkActive(true);} }}
                      onInclusion={() => { if(networkMode==="INCLUSION"){setNetworks([]);setClearNetworks(true);setSelectAllNetworks(false);setNetworkMode(null);setIsNetworkActive(false);}else{setNetworks([]);setClearNetworks(true);setSelectAllNetworks(false);setNetworkMode("INCLUSION");setIsNetworkActive(true);} }}
                      onClear={() => { setNetworks([]);setNetworkMode(null);setIsNetworkActive(false);setClearNetworks(true);setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllNetworks(c); if(c){setNetworks([]);setClearNetworks(true);} }); }} className="w-4 h-4 accent-purple-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={setNetworks} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if(insuranceMode==="EXCLUSION"){setInsuranceplan([]);setClearInsuranceplan(true);setSelectAllInsurance(false);setInsuranceMode(null);setIsInsuranceActive(false);}else{setInsuranceplan([]);setClearInsuranceplan(true);setSelectAllInsurance(false);setInsuranceMode("EXCLUSION");setIsInsuranceActive(true);} }}
                      onInclusion={() => { if(insuranceMode==="INCLUSION"){setInsuranceplan([]);setClearInsuranceplan(true);setSelectAllInsurance(false);setInsuranceMode(null);setIsInsuranceActive(false);}else{setInsuranceplan([]);setClearInsuranceplan(true);setSelectAllInsurance(false);setInsuranceMode("INCLUSION");setIsInsuranceActive(true);} }}
                      onClear={() => { setInsuranceplan([]);setInsuranceMode(null);setIsInsuranceActive(false);setClearInsuranceplan(true);setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllInsurance(c); if(c){setInsuranceplan([]);setClearInsuranceplan(true);} }); }} className="w-4 h-4 accent-amber-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={setInsuranceplan} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if(policyMode==="EXCLUSION"){setPolicy([]);setClearPolicy(true);setSelectAllPolicy(false);setPolicyMode(null);setIsPolicyActive(false);}else{setPolicy([]);setClearPolicy(true);setSelectAllPolicy(false);setPolicyMode("EXCLUSION");setIsPolicyActive(true);} }}
                      onInclusion={() => { if(policyMode==="INCLUSION"){setPolicy([]);setClearPolicy(true);setSelectAllPolicy(false);setPolicyMode(null);setIsPolicyActive(false);}else{setPolicy([]);setClearPolicy(true);setSelectAllPolicy(false);setPolicyMode("INCLUSION");setIsPolicyActive(true);} }}
                      onClear={() => { setPolicy([]);setPolicyMode(null);setIsPolicyActive(false);setClearPolicy(true);setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllPolicy(c); if(c){setPolicy([]);setClearPolicy(true);} }); }} className="w-4 h-4 accent-emerald-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <PolicyNames onChange={(v: PolicyItem[]) => setPolicy(v)} defaultValue={policy} clear={clearPolicy} />}
                    </EntityCard>

                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if(patientMode==="EXCLUSION"){setPatients([]);setClearPatient(true);setSelectAllPatients(false);setPatientMode(null);setIsPatientActive(false);}else{setPatients([]);setClearPatient(true);setSelectAllPatients(false);setPatientMode("EXCLUSION");setIsPatientActive(true);} }}
                      onInclusion={() => { if(patientMode==="INCLUSION"){setPatients([]);setClearPatient(true);setSelectAllPatients(false);setPatientMode(null);setIsPatientActive(false);}else{setPatients([]);setClearPatient(true);setSelectAllPatients(false);setPatientMode("INCLUSION");setIsPatientActive(true);} }}
                      onClear={() => { setPatients([]);setPatientMode(null);setIsPatientActive(false);setClearPatient(true);setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllPatients(c); if(c){setPatients([]);setClearPatient(true);} }); }} className="w-4 h-4 accent-pink-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <VipPatientSelector onChange={setPatients} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if(vipMode==="EXCLUSION"){setVip([]);setClearVip(true);setSelectAllVip(false);setVipMode(null);setIsVipActive(false);}else{setVip([]);setClearVip(true);setSelectAllVip(false);setVipMode("EXCLUSION");setIsVipActive(true);} }}
                      onInclusion={() => { if(vipMode==="INCLUSION"){setVip([]);setClearVip(true);setSelectAllVip(false);setVipMode(null);setIsVipActive(false);}else{setVip([]);setClearVip(true);setSelectAllVip(false);setVipMode("INCLUSION");setIsVipActive(true);} }}
                      onClear={() => { setVip([]);setVipMode(null);setIsVipActive(false);setClearVip(true);setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllVip(c); if(c){setVip([]);setClearVip(true);} }); }} className="w-4 h-4 accent-yellow-600" />
                        <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <Vip onChange={setVip} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    <EntityCard label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if(specialtyMode==="EXCLUSION"){setSpecialty([]);setClearSpecialty(true);setSelectAllSpecialty(false);setSpecialtyMode(null);setIsSpecialtyActive(false);}else{setSpecialty([]);setClearSpecialty(true);setSelectAllSpecialty(false);setSpecialtyMode("EXCLUSION");setIsSpecialtyActive(true);} }}
                      onInclusion={() => { if(specialtyMode==="INCLUSION"){setSpecialty([]);setClearSpecialty(true);setSelectAllSpecialty(false);setSpecialtyMode(null);setIsSpecialtyActive(false);}else{setSpecialty([]);setClearSpecialty(true);setSelectAllSpecialty(false);setSpecialtyMode("INCLUSION");setIsSpecialtyActive(true);} }}
                      onClear={() => { setSpecialty([]);setSpecialtyMode(null);setIsSpecialtyActive(false);setClearSpecialty(true);setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllSpecialty(c); if(c){setSpecialty([]);setClearSpecialty(true);} }); }} className="w-4 h-4 accent-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <ListOfSpecialtyName onChange={setSpecialty} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if(payerMode==="EXCLUSION"){setPayer([]);setSelectAllPayer(false);setPayerMode(null);setIsPayerActive(false);}else{setPayer([]);setSelectAllPayer(false);setPayerMode("EXCLUSION");setIsPayerActive(true);} }}
                      onInclusion={() => { if(payerMode==="INCLUSION"){setPayer([]);setSelectAllPayer(false);setPayerMode(null);setIsPayerActive(false);}else{setPayer([]);setSelectAllPayer(false);setPayerMode("INCLUSION");setIsPayerActive(true);} }}
                      onClear={() => { setPayer([]);setPayerMode(null);setIsPayerActive(false);setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllPayer(c); if(c) setPayer([]); }); }} className="w-4 h-4 accent-indigo-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={(e) => setPayer(e.target.value ? [e.target.value] : [])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if(userMode==="EXCLUSION"){setUserLevel([]);setSelectAllUser(false);setUserMode(null);setIsUserActive(false);}else{setUserLevel([]);setSelectAllUser(false);setUserMode("EXCLUSION");setIsUserActive(true);} }}
                      onInclusion={() => { if(userMode==="INCLUSION"){setUserLevel([]);setSelectAllUser(false);setUserMode(null);setIsUserActive(false);}else{setUserLevel([]);setSelectAllUser(false);setUserMode("INCLUSION");setIsUserActive(true);} }}
                      onClear={() => { setUserLevel([]);setUserMode(null);setIsUserActive(false);setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50">
                        <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c=e.target.checked; preserveScrollCheckbox(e,()=>{ setSelectAllUser(c); if(c) setUserLevel([]); }); }} className="w-4 h-4 accent-slate-600" />
                        <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input value={userLevel.join(",")} onChange={(e) => setUserLevel(e.target.value ? [e.target.value] : [])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Codes Popup */}
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list" /> {allCodesTitle}
              <span className="ml-auto text-sm font-normal text-gray-500">({allCodesItems.length+allCodesMedical.length+allCodesCustom.length} total)</span>
            </DialogTitle>
          </DialogHeader>
       <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {allCodesItems.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-500 mb-2">Activity Codes ({allCodesItems.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesItems.map((item, idx) => {
                    const col = getActivityTypeColor(item.type);
                    return (
                      <div key={idx} className={`flex items-center justify-between gap-2 p-2.5 bg-white rounded-lg border ${col.border}`}>
                        <span className={`font-bold text-sm ${col.text}`}>{item.code}</span>
                        <span className={`text-[10px] font-bold uppercase ${col.text} opacity-70`}>{item.type}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {allCodesMedical.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-500 mb-2">Medical Groups ({allCodesMedical.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesMedical.map((name, idx) => (
                    <div key={idx} className="flex items-center justify-center gap-2 p-2.5 bg-green-50 rounded-lg border border-green-300">
                      <span className="font-bold text-sm text-green-800">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {allCodesCustom.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-slate-500 mb-2">Custom Groups ({allCodesCustom.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {allCodesCustom.map((name, idx) => (
                    <div key={idx} className="flex items-center justify-center gap-2 p-2.5 bg-purple-50 rounded-lg border border-purple-300">
                      <span className="font-bold text-sm text-purple-800">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ICD Codes Dialog */}
      <Dialog open={icdCodesDialogOpen} onOpenChange={setIcdCodesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-stethoscope text-rose-500" />
              {icdCodesDialogTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {["icd","medical","custom"].map((kind) => {
              const filtered = icdCodesDialogList.filter(x => x.kind === kind as any);
              if (filtered.length === 0) return null;
              const styles: Record<string, any> = {
                icd:     { bg:"#011934", color:"white",   border:"#011934", label:"ICD Codes" },
                medical: { bg:"#f0fdf4", color:"#15803d", border:"#86efac", label:"Medical ICD Groups" },
                custom:  { bg:"#faf5ff", color:"#7e22ce", border:"#d8b4fe", label:"Custom ICD Groups" },
              };
              const s = styles[kind];
              return (
                <div key={kind}>
                  <p className="text-xs font-bold text-gray-600 mb-2">{s.label} ({filtered.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {filtered.map((c,i) => (<span key={i} style={{ padding:"4px 12px", background:s.bg, color:s.color, borderRadius:6, fontSize:12, fontWeight:600, border:`1px solid ${s.border}` }}>{c.label}</span>))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setIcdCodesDialogOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Force / Hard Delete Dialog ───────────────────────────────────── */}
      {forceDialogOpen && (
        <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>
          <div style={{ background:"#fff", borderRadius:16, padding:32, width:440, boxShadow:"0 20px 50px rgba(0,0,0,0.3)", border:"1px solid #fecaca" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:12 }}>
              <div style={{ width:60, height:60, borderRadius:"50%", background:"#fef2f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize:28, color:"#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize:18, fontWeight:700, color:"#1e293b", margin:0 }}>Permanent Delete</h3>
                  <p style={{ fontSize:13, color:"#64748b", margin:0, lineHeight:1.6 }}>
                    This will <strong style={{ color:"#dc2626" }}>permanently delete</strong> the record.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width:"100%", background:"#fef9c3", border:"1px solid #fde047", borderRadius:10, padding:"12px 16px", textAlign:"left" }}>
                      <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color:"#ca8a04", marginTop:2, flexShrink:0 }}></i>
                        <p style={{ fontSize:13, color:"#713f12", margin:0, lineHeight:1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:10, width:"100%", marginTop:4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex:1, background:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0", borderRadius:9, padding:12, cursor:"pointer", fontWeight:600, fontSize:13 }}>Cancel</button>
                    <button onClick={() => forceId !== null && permanentDeleteItem(forceId)}
                      style={{ flex:1, background:"#7f1d1d", color:"#fff", border:"none", borderRadius:9, padding:12, cursor:"pointer", fontWeight:700, fontSize:13 }}>
                      <i className="fa-solid fa-trash" style={{ marginRight:8 }}></i>Permanently Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityNotAllowedICD;
